import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { popOverlay, pushOverlay } from "@/lib/overlay-store";

export function BottomSheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  tall,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Sticky action area pinned to the bottom of the sheet. */
  footer?: ReactNode;
  /** Opens the sheet at ~88% of the screen height, like a native bottom sheet. */
  tall?: boolean;
}) {
  const [keyboardInset, setKeyboardInset] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    pushOverlay();
    return () => popOverlay();
  }, [open]);

  // Freeze the page behind the sheet so only the sheet scrolls.
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const body = document.body;
    const previous = body.style.overflow;
    body.style.overflow = "hidden";
    return () => {
      body.style.overflow = previous;
    };
  }, [open]);

  // Lift the sheet above the on-screen keyboard so inputs + action stay visible.
  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInset(inset > 80 ? inset : 0);
    };
    update();
    vv.addEventListener("resize", update, { passive: true });
    vv.addEventListener("scroll", update, { passive: true });
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      setKeyboardInset(0);
    };
  }, [open]);

  const content = (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-xl"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={{ y: "100%", opacity: 0.6 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0.4 }}
            transition={{ type: "spring", stiffness: 260, damping: 30, mass: 0.9 }}
            style={{ bottom: keyboardInset }}
            className={
              tall
                ? "glass fixed inset-x-0 z-[91] flex h-[92dvh] max-h-[92dvh] flex-col rounded-t-[2rem] px-6 pt-3"
                : "glass fixed inset-x-0 z-[91] flex max-h-[92dvh] flex-col rounded-t-[2rem] px-6 pt-3"
            }
          >
            <div className="mx-auto mb-5 h-1.5 w-11 shrink-0 rounded-full bg-white/20" />
            <h2 className="shrink-0 text-xl font-bold tracking-tight">{title}</h2>
            {subtitle ? (
              <p className="mt-1 shrink-0 text-sm text-muted-foreground">{subtitle}</p>
            ) : null}
            <div
              className="mt-5 min-h-0 flex-1 overflow-y-auto overscroll-contain"
              style={{ paddingBottom: footer ? 4 : "max(2.25rem, env(safe-area-inset-bottom))" }}
            >
              {children}
            </div>
            {footer ? (
              <div
                className="sticky bottom-0 shrink-0 bg-gradient-to-t from-background/95 via-background/85 to-transparent pt-3"
                style={{ paddingBottom: "max(1.5rem, calc(env(safe-area-inset-bottom) + 0.75rem))" }}
              >
                {footer}
              </div>
            ) : null}
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );

  // Render in a portal on <body> so the sheet is never positioned relative to a
  // transformed/animated ancestor (which made it open partially, tied to scroll).
  if (!mounted || typeof document === "undefined") return null;
  return createPortal(content, document.body);
}
